<div class="p-6 lg:p-8">

  <!-- Breadcrumb + page title (matches the AWS detail shell) -->
  <div class="mb-5">
    <nav class="mb-2 flex items-center gap-1.5 text-xs" aria-label="Breadcrumb">
      <a routerLink="/cloud" [queryParams]="{ provider: 'azure', account: subscription, region: location }"
         class="rounded font-medium text-slate-500 transition-colors hover:text-violet-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400">Cloud</a>
      <lucide-icon name="chevron-right" [size]="12" class="text-slate-300" [strokeWidth]="2"></lucide-icon>
      <span class="font-medium text-slate-500">Azure</span>
      <lucide-icon name="chevron-right" [size]="12" class="text-slate-300" [strokeWidth]="2"></lucide-icon>
      <span class="font-medium text-slate-600">{{ title }}</span>
    </nav>

    <div class="flex items-center gap-2.5">
      <a routerLink="/cloud" [queryParams]="{ provider: 'azure', account: subscription, region: location }"
         class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
         aria-label="Back to Cloud overview">
        <lucide-icon name="arrow-left" [size]="16" [strokeWidth]="2"></lucide-icon>
      </a>
      <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
        <lucide-icon [name]="icon" [size]="18" class="text-slate-600" [strokeWidth]="1.75"></lucide-icon>
      </div>
      <h1 class="text-xl font-semibold text-slate-900">{{ title }}</h1>

      <!-- Scope context (subscription + location) + refresh — right-anchored -->
      <div class="ml-auto flex flex-wrap items-center gap-2">
        <span class="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600" [title]="subscription">
          <lucide-icon name="cloud" [size]="12" class="text-slate-400" [strokeWidth]="1.75"></lucide-icon>
          {{ subscriptionLabel || subscription || '—' }}
        </span>
        <span class="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
          <lucide-icon name="map-pin" [size]="12" class="text-slate-400" [strokeWidth]="1.75"></lucide-icon>
          {{ locationScopeLabel }}
        </span>
        <button type="button" (click)="refresh()" [disabled]="refreshing"
          class="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label="Refresh data">
          <lucide-icon name="refresh-cw" [size]="13" [strokeWidth]="2" [class.animate-spin]="refreshing"></lucide-icon>
          {{ refreshing ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>
    </div>
  </div>

  <div *ngIf="toggleError" class="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2" role="alert">
    <lucide-icon name="triangle-alert" [size]="14" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
    <p class="text-xs text-red-700">{{ toggleError }}</p>
  </div>

  <!-- Content card: tabs + panels -->
  <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <app-cloud-tabs [tabs]="tabs" [active]="selectedTab" (tabChange)="selectedTab = $any($event)"></app-cloud-tabs>

    <!-- ── Overview ─────────────────────────────────────────────────── -->
    <div *ngIf="selectedTab === 'overview'" class="p-6" role="tabpanel">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p class="text-xs font-medium uppercase tracking-wide text-slate-400">{{ metricLabel }}</p>
          <p class="mt-1 text-2xl font-bold text-slate-900">{{ totalCount }}</p>
        </div>
        <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <p class="text-xs font-medium uppercase tracking-wide text-emerald-500">Monitored</p>
          <p class="mt-1 text-2xl font-bold text-emerald-700">{{ monitoredCount }}</p>
        </div>
        <div class="rounded-lg border p-4"
             [class.border-red-200]="issues24hTotal > 0" [class.bg-red-50]="issues24hTotal > 0"
             [class.border-slate-200]="issues24hTotal === 0" [class.bg-slate-50]="issues24hTotal === 0">
          <p class="text-xs font-medium uppercase tracking-wide" [class.text-red-500]="issues24hTotal > 0" [class.text-slate-400]="issues24hTotal === 0">Issues (24h)</p>
          <p class="mt-1 text-2xl font-bold" [class.text-red-700]="issues24hTotal > 0" [class.text-slate-900]="issues24hTotal === 0">{{ issues24hTotal }}</p>
          <p class="mt-1 text-[11px] text-slate-400">From monitoring findings</p>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p class="text-xs font-medium uppercase tracking-wide text-slate-400">Locations</p>
          <p class="mt-1 text-2xl font-bold text-slate-900">{{ distinctLocationCount }}</p>
        </div>
      </div>

      <div *ngIf="isGlobal" class="mt-4 flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-700">
        <lucide-icon name="globe" [size]="13" [strokeWidth]="2"></lucide-icon>
        {{ title }} is a global Azure service — resources are discovered across the whole subscription regardless of the selected location.
      </div>
    </div>

    <!-- ── Resources ────────────────────────────────────────────────── -->
    <div *ngIf="selectedTab === 'resources'" role="tabpanel">
      <app-cloud-filter-bar
        [entityName]="metricLabel"
        placeholder="Search by name or resource group…"
        [facets]="facets"
        [sortOptions]="sortOptions"
        [filters]="filters"
        [resultCount]="filteredResources.length"
        [totalCount]="resources.length"
        [quotaUsage]="quotaUsage"
        (filtersChange)="onFiltersChange($event)"
      ></app-cloud-filter-bar>

      <div class="flex min-h-0" [class.divide-x]="selectedResource" [class.divide-slate-200]="selectedResource">

        <!-- Table area -->
        <div class="min-w-0 flex-1 p-6">

          <div *ngIf="loading" class="flex items-center gap-2 py-6 text-sm text-slate-400">
            <lucide-icon name="loader" [size]="16" class="animate-spin" [strokeWidth]="2"></lucide-icon>
            Discovering {{ title }} resources…
          </div>

          <div *ngIf="!loading && error" class="flex items-start gap-3 py-6" role="alert">
            <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-amber-500" [strokeWidth]="2"></lucide-icon>
            <div>
              <p class="text-sm text-slate-700">Couldn't list {{ title }} resources.</p>
              <p class="mt-0.5 text-xs text-slate-500">{{ error }}</p>
            </div>
          </div>

          <div *ngIf="!loading && !error" class="overflow-x-auto">
            <table class="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr class="border-b border-slate-200">
                  <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">{{ metricLabel }}</th>
                  <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Resource group</th>
                  <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Location</th>
                  <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Kind / SKU</th>
                  <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Status</th>
                  <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Issues (24h)</th>
                  <th class="pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Monitoring</th>
                  <th class="pb-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400"></th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let r of pagedResources" class="border-b border-slate-100 last:border-0" [class.bg-violet-50]="r.id === selectedResourceId">
                  <td class="py-3 pr-4">
                    <p class="max-w-[22rem] truncate text-xs font-medium text-slate-900" [title]="r.name">{{ r.name }}</p>
                    <p class="max-w-[22rem] truncate font-mono text-[11px] text-slate-400" [title]="r.id">{{ r.id }}</p>
                  </td>
                  <td class="whitespace-nowrap py-3 pr-4 text-xs text-slate-600">{{ r.resourceGroup || '—' }}</td>
                  <td class="whitespace-nowrap py-3 pr-4 text-xs text-slate-600">{{ r.location }}</td>
                  <td class="whitespace-nowrap py-3 pr-4 text-xs text-slate-600">{{ kindOrSku(r) }}</td>
                  <td class="py-3 pr-4"><app-status-badge [status]="statusHealth(r)"></app-status-badge></td>
                  <td class="whitespace-nowrap py-3 pr-4 text-xs tabular-nums">
                    <span *ngIf="issueCount(r.id) > 0; else noIssues" class="font-semibold text-red-600">{{ issueCount(r.id) }}</span>
                    <ng-template #noIssues><span class="text-slate-300">—</span></ng-template>
                  </td>
                  <!-- Monitoring toggle chip (matches AWS) -->
                  <td class="whitespace-nowrap py-3 pr-4">
                    <ng-container *ngIf="isMonitored(r); else notMon">
                      <button type="button" (click)="toggle(r)" [disabled]="isToggling(r)"
                        class="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:opacity-60"
                        aria-label="Disable monitoring">
                        <lucide-icon *ngIf="isToggling(r)" name="loader" [size]="10" class="animate-spin" [strokeWidth]="2"></lucide-icon>
                        <span *ngIf="!isToggling(r)" class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
                        Monitored
                      </button>
                    </ng-container>
                    <ng-template #notMon>
                      <ng-container *ngIf="canMonitorMore; else quotaFull">
                        <button type="button" (click)="toggle(r)" [disabled]="isToggling(r)"
                          class="rounded border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-60">
                          Monitor
                        </button>
                      </ng-container>
                      <ng-template #quotaFull>
                        <span class="group relative inline-block">
                          <button type="button" (click)="toggle(r)" aria-disabled="true"
                            class="cursor-not-allowed rounded border border-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-300 focus:outline-none">Monitor</button>
                          <span class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-[10px] leading-tight text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">Quota reached</span>
                        </span>
                      </ng-template>
                    </ng-template>
                  </td>
                  <td class="py-3 text-right">
                    <button (click)="r.id === selectedResourceId ? closeResource() : selectResource(r.id)"
                      class="rounded px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
                      [class.text-violet-600]="r.id === selectedResourceId">
                      {{ r.id === selectedResourceId ? 'Close' : 'View details' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Empty: no matches -->
            <div *ngIf="filteredResources.length === 0 && resources.length > 0" class="py-10 text-center">
              <lucide-icon name="search" [size]="28" class="mx-auto mb-2 text-slate-300" [strokeWidth]="1.5"></lucide-icon>
              <p class="text-sm font-medium text-slate-500">No {{ title }} resources match your filters</p>
              <p class="mt-0.5 text-xs text-slate-400">Try adjusting the search or clearing active filters.</p>
            </div>

            <!-- Empty: no data (connected, none found) -->
            <div *ngIf="resources.length === 0" class="flex flex-col items-center gap-3 py-12 text-center">
              <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100">
                <lucide-icon [name]="icon" [size]="22" class="text-slate-400" [strokeWidth]="1.5"></lucide-icon>
              </div>
              <div>
                <p class="text-sm font-semibold text-slate-900">No {{ title }} resources found</p>
                <p class="mt-1 text-sm text-slate-500">Nothing to monitor in this subscription and location scope yet.</p>
              </div>
            </div>

            <!-- Pagination -->
            <div *ngIf="totalPages > 1" class="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
              <p class="text-xs text-slate-400">{{ pageStart }}–{{ pageEnd }} of {{ filteredResources.length }}</p>
              <div class="flex items-center gap-1">
                <button (click)="prevPage()" [disabled]="currentPage === 1"
                  class="rounded px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                  [class.cursor-not-allowed]="currentPage === 1" [class.text-slate-300]="currentPage === 1" [class.text-slate-600]="currentPage !== 1" [class.hover:bg-slate-100]="currentPage !== 1">Previous</button>
                <button *ngFor="let p of pageNumbers" (click)="goToPage(p)"
                  class="min-w-[28px] rounded px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                  [class.bg-violet-600]="p === currentPage" [class.text-white]="p === currentPage" [class.text-slate-600]="p !== currentPage" [class.hover:bg-slate-100]="p !== currentPage">{{ p }}</button>
                <button (click)="nextPage()" [disabled]="currentPage === totalPages"
                  class="rounded px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-violet-300"
                  [class.cursor-not-allowed]="currentPage === totalPages" [class.text-slate-300]="currentPage === totalPages" [class.text-slate-600]="currentPage !== totalPages" [class.hover:bg-slate-100]="currentPage !== totalPages">Next</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Resource detail side panel -->
        <div *ngIf="selectedResource" class="flex w-96 shrink-0 flex-col">
          <div class="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <div class="flex min-w-0 items-center gap-2">
              <lucide-icon [name]="icon" [size]="14" class="shrink-0 text-slate-400" [strokeWidth]="2"></lucide-icon>
              <span class="truncate text-xs font-semibold text-slate-800" [title]="selectedResource.name">{{ selectedResource.name }}</span>
            </div>
            <button (click)="closeResource()" class="ml-2 shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300" aria-label="Close details">
              <lucide-icon name="x" [size]="14" [strokeWidth]="2"></lucide-icon>
            </button>
          </div>

          <div class="border-b border-slate-100 px-4 py-3">
            <div class="grid grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <p class="text-[10px] font-medium uppercase tracking-wide text-slate-400">Resource group</p>
                <p class="mt-0.5 truncate text-xs text-slate-700" [title]="selectedResource.resourceGroup || '—'">{{ selectedResource.resourceGroup || '—' }}</p>
              </div>
              <div>
                <p class="text-[10px] font-medium uppercase tracking-wide text-slate-400">Location</p>
                <p class="mt-0.5 text-xs text-slate-700">{{ selectedResource.location }}</p>
              </div>
              <div>
                <p class="text-[10px] font-medium uppercase tracking-wide text-slate-400">Kind / SKU</p>
                <p class="mt-0.5 truncate text-xs text-slate-700" [title]="kindOrSku(selectedResource)">{{ kindOrSku(selectedResource) }}</p>
              </div>
              <div>
                <p class="text-[10px] font-medium uppercase tracking-wide text-slate-400">Status</p>
                <div class="mt-0.5"><app-status-badge [status]="statusHealth(selectedResource)"></app-status-badge></div>
              </div>
              <div class="col-span-2">
                <p class="text-[10px] font-medium uppercase tracking-wide text-slate-400">Resource ID</p>
                <p class="mt-0.5 break-all font-mono text-[11px] text-slate-500">{{ selectedResource.id }}</p>
              </div>
            </div>
          </div>

          <p class="border-b border-slate-100 px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">Recent findings</p>
          <div class="flex-1 overflow-y-auto">
            <div *ngIf="selectedResourceFindings.length === 0" class="px-4 py-6 text-xs text-slate-400">
              No findings for this resource. Monitor it to start collecting Azure Monitor health signals.
            </div>
            <ul *ngIf="selectedResourceFindings.length > 0" class="divide-y divide-slate-100">
              <li *ngFor="let f of selectedResourceFindings" class="px-4 py-2.5">
                <div class="flex items-center justify-between gap-2">
                  <span class="text-xs font-medium text-slate-800">{{ f.title }}</span>
                  <span class="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
                    [class.bg-red-100]="f.severity === 'CRITICAL' || f.severity === 'HIGH'" [class.text-red-700]="f.severity === 'CRITICAL' || f.severity === 'HIGH'"
                    [class.bg-amber-100]="f.severity === 'MEDIUM'" [class.text-amber-700]="f.severity === 'MEDIUM'"
                    [class.bg-slate-100]="f.severity === 'LOW' || f.severity === 'INFO'" [class.text-slate-600]="f.severity === 'LOW' || f.severity === 'INFO'">{{ f.severity }}</span>
                </div>
                <p class="mt-0.5 text-[11px] text-slate-500">{{ f.summary }}</p>
                <p class="mt-0.5 text-[10px] text-slate-400">{{ f.createdAt | cloudDate }} · {{ f.findingState }}</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Activity ─────────────────────────────────────────────────── -->
    <div *ngIf="selectedTab === 'activity'" role="tabpanel">
      <app-cloud-activity-feed
        [items]="activity"
        [loading]="activityLoading"
        [serviceLabel]="title"
        emptyHint="Health findings for this Azure service appear here as resources are monitored."
        [note]="'Activity is derived from Azure Monitor findings — per-request traces aren\'t collected.'"
        (itemSelect)="onActivitySelect($event)"
      ></app-cloud-activity-feed>
    </div>

  </div>
</div>
